// Admin → Guardrails & Safety — the tenant-wide FLOOR (CC-1 / Batch TS3).
//
// This is the org-wide guardrail policy: blocked topics, PII handling, prompt-
// injection, output prohibited phrases, and the on-block action. Every agent in
// the workspace inherits these as a MINIMUM — a per-agent config (in the Agent
// Builder / Detail Viewer) unions with this floor and may only TIGHTEN it, never
// loosen it below. Replaces the old process-global blocked-topics env (which was
// never tenant-scoped).

import { useState, useEffect, useCallback } from "react";
import { COLORS } from "../../theme/colors";
import { authApi } from "../../api/client";
import ChipInputShared from "../../components/ChipInput";

// Re-export so existing imports (and tests) keep working after the extraction.
export const ChipInput = ChipInputShared;

const BORDER = COLORS.border || "#e2e5eb";
const DIM = COLORS.textDim || "#9090a8";
const GREEN = COLORS.green || "#10b981";
const RED = COLORS.red || "#ef4444";
const ACCENT = COLORS.accent || "#3b82f6";

const card = {
  border: `1px solid ${BORDER}`, borderRadius: 12, background: COLORS.surface,
  padding: "16px 18px", marginBottom: 14,
};
const labelStyle = { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: DIM, display: "block", marginBottom: 8 };
const helpStyle = { fontSize: 12, color: DIM, margin: "4px 0 0", lineHeight: 1.5 };

function unwrap(res) {
  return res && res.data && typeof res.data === "object" ? res.data : res;
}

function Toggle({ checked, disabled, onChange, label }) {
  const on = !!checked;
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled} onClick={() => onChange(!on)} style={{ position: "relative", width: 44, height: 24, flex: "none", borderRadius: 999, border: `1px solid ${on ? ACCENT : BORDER}`, background: on ? ACCENT : (COLORS.surfaceHover || "#f1f5f9"), cursor: disabled ? "not-allowed" : "pointer", padding: 0, opacity: disabled ? 0.55 : 1, transition: "background .15s, border-color .15s" }}>
      <span style={{ position: "absolute", top: 2, left: on ? 22 : 2, width: 18, height: 18, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 2px rgba(0,0,0,.3)", transition: "left .15s" }} />
    </button>
  );
}

const SELECT = {
  fontSize: 13, padding: "7px 9px", borderRadius: 8,
  border: `1px solid ${BORDER}`, background: COLORS.surface, color: COLORS.text, minWidth: 180,
};

export default function GuardrailsSafetyTab({ canEdit = true }) {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = unwrap(await authApi.getGuardrailsConfig());
      setForm({ blocked_topics: data.blocked_topics || [], prohibited_phrases: data.prohibited_phrases || [], pii_action: data.pii_action || "mask", injection_detection: data.injection_detection !== false, on_block_action: data.on_block_action || "message", on_block_message: data.on_block_message || "", fact_checker_level: data.fact_checker_level || "standard", fact_checker_threshold: data.fact_checker_threshold ?? 0.75 });
    } catch {
      setError("Couldn't load the Guardrails & Safety policy.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setSaved(false); };

  const save = useCallback(async () => {
    if (saving || !form) return;
    setSaving(true); setError(null); setSaved(false);
    try {
      await authApi.updateGuardrailsConfig(form);
      setSaved(true);
    } catch {
      setError("Couldn't save the Guardrails & Safety policy.");
    } finally {
      setSaving(false);
    }
  }, [saving, form]);

  if (loading) return <div style={{ padding: 20, color: DIM, fontSize: 13 }}>Loading Guardrails & Safety…</div>;
  if (error && !form) return <div style={{ padding: 20, color: RED, fontSize: 13 }}>{error}</div>;

  const f = form;
  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, margin: 0, color: COLORS.text }}>Guardrails &amp; Safety</h2>
        <p style={{ margin: "5px 0 0", color: DIM, fontSize: 13, maxWidth: "72ch", lineHeight: 1.55 }}>
          The org-wide safety <strong>floor</strong>. Every agent in this workspace inherits these settings as a
          minimum — an individual agent can tighten them (add more blocked topics, a stricter PII action) but can
          never fall below this floor.
        </p>
      </div>

      {/* ── Input: what customers send in ───────────────────────── */}
      <div style={card}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: COLORS.text, marginBottom: 12 }}>Input — what customers send</div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Blocked topics</label>
          <ChipInput value={f.blocked_topics} onChange={(v) => set("blocked_topics", v)}
            placeholder="e.g. weapons, self-harm — Enter to add" disabled={!canEdit} tone={RED} />
          <p style={helpStyle}>A message that mentions any of these is refused before it reaches the model. Applies to input and output.</p>
        </div>

        <div style={{ marginBottom: 16, display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 260px" }}>
            <label style={labelStyle}>PII handling</label>
            <select value={f.pii_action} disabled={!canEdit} style={SELECT}
              onChange={(e) => set("pii_action", e.target.value)}>
              <option value="mask">Mask — redact detected PII (recommended)</option>
              <option value="block">Block — refuse the whole message</option>
              <option value="flag">Flag — record only, don't alter</option>
            </select>
            <p style={helpStyle}>How detected PII (cards, SSNs, emails, phones) is handled on the way in.</p>
          </div>
          <div style={{ flex: "0 0 auto" }}>
            <label style={labelStyle}>Prompt-injection detection</label>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Toggle checked={f.injection_detection} disabled={!canEdit} label="Prompt-injection detection"
                onChange={(v) => set("injection_detection", v)} />
              <span style={{ fontSize: 12.5, color: DIM }}>{f.injection_detection ? "On" : "Off"}</span>
            </div>
            <p style={helpStyle}>Catches "ignore previous instructions"-style attacks.</p>
          </div>
        </div>
      </div>

      {/* ── Output: what agents say back ────────────────────────── */}
      <div style={card}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: COLORS.text, marginBottom: 12 }}>Output — what agents say back</div>
        <div style={{ marginBottom: 4 }}>
          <label style={labelStyle}>Prohibited phrases</label>
          <ChipInput value={f.prohibited_phrases} onChange={(v) => set("prohibited_phrases", v)}
            placeholder="phrases the agent must never say — Enter to add" disabled={!canEdit} tone={RED} />
          <p style={helpStyle}>If a reply contains any of these, it's replaced with a safe fallback and flagged for review — the customer never sees the flagged text. Grounding / hallucination checks live in the Fact Checker, not here.</p>
        </div>
      </div>

      {/* ── Fact Checking (org default) ─────────────────────────── */}
      <div style={card}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: COLORS.text, marginBottom: 12 }}>Fact Checking — org default</div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 240px" }}>
            <label style={labelStyle}>Default mode</label>
            <select value={f.fact_checker_level} disabled={!canEdit} style={SELECT}
              onChange={(e) => set("fact_checker_level", e.target.value)}>
              <option value="off">Off — no verification</option>
              <option value="standard">Standard — every answer backed by evidence (recommended)</option>
              <option value="strict">Strict — a second model confirms anything uncertain</option>
            </select>
            <p style={helpStyle}>The floor for every agent. An agent may go stricter, never below this.</p>
          </div>
          {f.fact_checker_level !== "off" && (
            <div style={{ flex: "1 1 200px" }}>
              <label style={labelStyle}>Min pass threshold: {Number(f.fact_checker_threshold).toFixed(2)}</label>
              <input type="range" min={0} max={1} step={0.05} value={f.fact_checker_threshold} disabled={!canEdit}
                onChange={(e) => set("fact_checker_threshold", Number(e.target.value))}
                style={{ width: "100%" }} />
              <p style={helpStyle}>Share of claims that must be grounded to pass. Higher = stricter.</p>
            </div>
          )}
        </div>
      </div>

      {/* ── On block ────────────────────────────────────────────── */}
      <div style={card}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: COLORS.text, marginBottom: 12 }}>When a guardrail blocks</div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 240px" }}>
            <label style={labelStyle}>Action</label>
            <select value={f.on_block_action} disabled={!canEdit} style={SELECT}
              onChange={(e) => set("on_block_action", e.target.value)}>
              <option value="message">Show a safe message</option>
              <option value="connect_to_human">Connect to a human (escalate)</option>
              <option value="both">Both — message and escalate</option>
            </select>
            <p style={helpStyle}>"Connect to a human" reuses your existing Escalation / Handoff setup.</p>
          </div>
          <div style={{ flex: "1 1 240px" }}>
            <label style={labelStyle}>Message (optional)</label>
            <input value={f.on_block_message} disabled={!canEdit}
              onChange={(e) => set("on_block_message", e.target.value)}
              placeholder="Leave blank for the default safe reply"
              style={{ ...SELECT, minWidth: 0, width: "100%" }} />
          </div>
        </div>
      </div>

      {canEdit && (
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 4 }}>
          <button type="button" onClick={save} disabled={saving}
            style={{
              background: ACCENT, color: "#fff", border: "none", borderRadius: 8,
              padding: "9px 20px", fontSize: 13.5, fontWeight: 600,
              cursor: saving ? "wait" : "pointer", opacity: saving ? 0.7 : 1,
            }}>{saving ? "Saving…" : "Save policy"}</button>
          {saved && <span style={{ color: GREEN, fontSize: 13, fontWeight: 600 }}>✓ Saved</span>}
          {error && <span style={{ color: RED, fontSize: 13 }}>{error}</span>}
        </div>
      )}
    </div>
  );
}
